<script setup lang="ts">
import { type Client, type DownloadDriver, type DriverConfig, clientsApi, downloadDriversApi, driverConfigsApi } from '@/api/entities'
import { driverConfigActionsApi } from '@/api/entities/review/driverConfigActions'
import { clientActionsApi } from '@/api/entities/review/clientActions'
import { getCustomFields } from '@/config/reviewSolutionCustomFields'

definePage({
  meta: {
    action: 'read',
    subject: 'Client',
  },
})

const { snackbar, handleError } = useErrorHandler()

const route = useRoute()
const router = useRouter()

const clientId = computed(() => route.params.id as string)
const client = ref<Client | null>(null)
const driverConfigs = ref<DriverConfig[]>([])
const downloadDrivers = ref<DownloadDriver[]>([])
const loading = ref(true)
const configsLoading = ref(false)

// 채널 추가 다이얼로그
const addChannelDialog = ref(false)
const selectedDownloadDriver = ref<number | null>(null)
const addingChannel = ref(false)

// 클라이언트 정보 로드
const loadClient = async () => {
  loading.value = true
  try {
    const response = await clientsApi.getClient(clientId.value)
    if (response.success && response.data) {
      const data = response.data as any

      client.value = data.data || data
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '고객사 정보를 불러오는데 실패했습니다.' })
  }
  finally {
    loading.value = false
  }
}

// 드라이버 설정 로드
const loadDriverConfigs = async () => {
  configsLoading.value = true
  try {
    const response = await driverConfigsApi.listDriverConfigs(clientId.value)
    if (response.success && response.data) {
      const data = response.data as any

      driverConfigs.value = data.data || data
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
  finally {
    configsLoading.value = false
  }
}

// 다운로드 드라이버 목록 로드
const loadDownloadDrivers = async () => {
  try {
    const response = await downloadDriversApi.listDownloadDrivers({ limit: 100 })
    if (response.success && response.data) {
      const data = response.data as any

      downloadDrivers.value = data.data || data
    }
  }
  catch (error) {
    handleError(error, { silent: true })
  }
}

// 초기화
onMounted(() => {
  loadClient()
  loadDriverConfigs()
  loadDownloadDrivers()
})

// 수정 페이지로 이동
const goToEdit = () => {
  router.push({ name: 'review-client-id-edit', params: { id: clientId.value } })
}

// 고객사 삭제
const deleteClient = async () => {
  if (!confirm('정말로 이 고객사를 삭제하시겠습니까?'))
    return

  try {
    const response = await clientsApi.deleteClient(clientId.value)
    if (response.success)
      router.push({ name: 'review-client' })
  }
  catch (error) {
    handleError(error, { notificationMessage: '고객사 삭제에 실패했습니다.' })
  }
}

// 채널 추가 다이얼로그 열기
const openAddChannelDialog = () => {
  selectedDownloadDriver.value = null
  addChannelDialog.value = true
}

// 채널 추가
const addChannel = async () => {
  if (!selectedDownloadDriver.value)
    return

  addingChannel.value = true
  try {
    const response = await driverConfigsApi.createDriverConfig(clientId.value, {
      download_driver_id: selectedDownloadDriver.value,
    } as any)

    if (response.success) {
      addChannelDialog.value = false
      snackbar.success('채널이 추가되었습니다.')
      await loadDriverConfigs()
    }
    else {
      snackbar.error('채널 추가에 실패했습니다.')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '채널 추가에 실패했습니다.' })
  }
  finally {
    addingChannel.value = false
  }
}

// 채널 삭제
const deleteDriverConfig = async (config: DriverConfig) => {
  const channelName = config.name || config.download_driver?.name || '채널'
  if (!confirm(`${channelName}을(를) 삭제하시겠습니까?\n관련 상품 및 리뷰 매핑 데이터가 모두 삭제됩니다.`))
    return

  try {
    const response = await driverConfigsApi.deleteDriverConfig(clientId.value, config.id)
    if (response.success) {
      snackbar.success('채널이 삭제되었습니다.')
      await loadDriverConfigs()
    }
    else {
      snackbar.error('채널 삭제에 실패했습니다.')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '채널 삭제에 실패했습니다.' })
  }
}

// 사용 가능한 다운로드 드라이버 (이미 추가된 것 제외)
const availableDrivers = computed(() => {
  const existingCodes = new Set(driverConfigs.value.map(c => c.driver_code))

  return downloadDrivers.value.filter(d => !existingCodes.has(d.code))
})

// 선택된 리뷰 솔루션의 커스텀 필드 (표시용)
const customFields = computed(() => getCustomFields(client.value?.review_service || null))

// 드라이버 설정 테이블 헤더
const configHeaders = [
  { title: 'ID', key: 'id' },
  { title: '채널명', key: 'name' },
  { title: '다운로드 드라이버', key: 'download_driver' },
  { title: '드라이버 코드', key: 'driver_code' },
  { title: '작업', key: 'actions', sortable: false },
]

// 운영 액션
const actionLoading = ref<string | null>(null)

const runDriverAction = async (
  actionName: keyof typeof driverConfigActionsApi,
  driverConfig: DriverConfig,
) => {
  actionLoading.value = `${actionName}-${driverConfig.id}`
  try {
    const result = await driverConfigActionsApi[actionName](driverConfig.id)
    if (result.success) {
      snackbar.success('액션이 완료되었습니다.')
    }
    else {
      snackbar.error('액션 실행에 실패했습니다.')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '액션 실행 중 오류가 발생했습니다.' })
  }
  finally {
    actionLoading.value = null
  }
}

const confirmDriverAction = (
  message: string,
  actionName: keyof typeof driverConfigActionsApi,
  driverConfig: DriverConfig,
) => {
  if (confirm(message))
    runDriverAction(actionName, driverConfig)
}

// 클라이언트 운영 액션
const clientActionLoading = ref<string | null>(null)

const runClientAction = async (actionName: keyof typeof clientActionsApi) => {
  if (actionName === 'bulkUpdateMonthlyState')
    return

  const id = Number(clientId.value)

  clientActionLoading.value = actionName
  try {
    const result = await (clientActionsApi[actionName] as (id: number) => ReturnType<typeof clientActionsApi.updateUploadReport>)(id)
    if (result.success) {
      snackbar.success('액션이 완료되었습니다.')
    }
    else {
      snackbar.error('액션 실행에 실패했습니다.')
    }
  }
  catch (error) {
    handleError(error, { notificationMessage: '액션 실행 중 오류가 발생했습니다.' })
  }
  finally {
    clientActionLoading.value = null
  }
}

const confirmClientAction = (message: string, actionName: keyof typeof clientActionsApi) => {
  if (confirm(message))
    runClientAction(actionName)
}
</script>

<template>
  <div>
    <!-- 로딩 상태 -->
    <VCard
      v-if="loading"
      class="mb-6"
    >
      <VCardText class="text-center pa-6">
        <VProgressCircular indeterminate />
        <p class="mt-4">
          고객사 정보를 불러오는 중...
        </p>
      </VCardText>
    </VCard>

    <!-- 고객사 정보 -->
    <template v-else-if="client">
      <VCard class="mb-6">
        <VCardTitle class="d-flex align-center justify-space-between pa-6">
          <div class="d-flex align-center gap-4">
            <VIcon
              icon="tabler-building"
              size="32"
            />
            <div>
              <h4 class="text-h4">
                {{ client.name }}
              </h4>
              <VChip
                :color="client.type === 'widget' ? 'success' : 'info'"
                size="small"
                class="mt-1"
              >
                {{ client.type === 'widget' ? '위젯' : '이관' }}
              </VChip>
            </div>
          </div>
          <div class="d-flex gap-2">
            <VBtn
              color="primary"
              variant="outlined"
              @click="goToEdit"
            >
              <VIcon
                start
                icon="tabler-edit"
              />
              수정
            </VBtn>
            <VBtn
              color="error"
              variant="outlined"
              @click="deleteClient"
            >
              <VIcon
                start
                icon="tabler-trash"
              />
              삭제
            </VBtn>
          </div>
        </VCardTitle>

        <VDivider />

        <VCardText>
          <VRow>
            <VCol
              cols="12"
              md="6"
            >
              <VList>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-database" />
                  </template>
                  <VListItemTitle>DB명</VListItemTitle>
                  <VListItemSubtitle>{{ client.target_database_hive || '-' }}</VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-world" />
                  </template>
                  <VListItemTitle>PC 도메인</VListItemTitle>
                  <VListItemSubtitle>{{ client.host || '-' }}</VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-upload" />
                  </template>
                  <VListItemTitle>업로드 드라이버</VListItemTitle>
                  <VListItemSubtitle>{{ client.upload_driver?.driver_name || client.review_service || '-' }}</VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-star" />
                  </template>
                  <VListItemTitle>별점 기준</VListItemTitle>
                  <VListItemSubtitle>
                    <VRating
                      :model-value="Number(client.score) || 0"
                      readonly
                      density="compact"
                    />
                  </VListItemSubtitle>
                </VListItem>
              </VList>
            </VCol>
            <VCol
              cols="12"
              md="6"
            >
              <VList>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-file-text" />
                  </template>
                  <VListItemTitle>텍스트 리뷰 이관</VListItemTitle>
                  <VListItemSubtitle>
                    <VIcon
                      :icon="client.text === 'Y' ? 'tabler-check' : 'tabler-x'"
                      :color="client.text === 'Y' ? 'success' : 'error'"
                    />
                  </VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-photo" />
                  </template>
                  <VListItemTitle>포토 리뷰 이관</VListItemTitle>
                  <VListItemSubtitle>
                    <VIcon
                      :icon="client.photo === 'Y' ? 'tabler-check' : 'tabler-x'"
                      :color="client.photo === 'Y' ? 'success' : 'error'"
                    />
                  </VListItemSubtitle>
                </VListItem>
                <VListItem>
                  <template #prepend>
                    <VIcon icon="tabler-message" />
                  </template>
                  <VListItemTitle>예상 리뷰 수</VListItemTitle>
                  <VListItemSubtitle>{{ client.expected_review_count?.toLocaleString() || 0 }}개</VListItemSubtitle>
                </VListItem>
              </VList>
            </VCol>
          </VRow>
        </VCardText>
      </VCard>

      <!-- 솔루션 추가 설정 (customFields가 있을 때만 표시) -->
      <VCard
        v-if="customFields.length > 0 && client.upload_driver_config"
        class="mb-6"
      >
        <VCardTitle class="pa-6">
          솔루션 추가 설정
        </VCardTitle>
        <VDivider />
        <VCardText>
          <VList>
            <VListItem
              v-for="field in customFields"
              :key="field.name"
            >
              <VListItemTitle>{{ field.title }}</VListItemTitle>
              <VListItemSubtitle>
                <template v-if="field.type === 'password'">
                  ••••••••
                </template>
                <template v-else-if="field.type === 'checkbox'">
                  <VIcon
                    :icon="client.upload_driver_config[field.name] === 'Y' || client.upload_driver_config[field.name] === true ? 'tabler-check' : 'tabler-x'"
                    :color="client.upload_driver_config[field.name] === 'Y' || client.upload_driver_config[field.name] === true ? 'success' : 'error'"
                  />
                </template>
                <template v-else>
                  {{ client.upload_driver_config[field.name] || '-' }}
                </template>
              </VListItemSubtitle>
            </VListItem>
          </VList>
        </VCardText>
      </VCard>

      <!-- 드라이버 설정 -->
      <VCard class="mb-6">
        <VCardTitle class="d-flex align-center justify-space-between pa-6">
          <span>채널 설정</span>
          <VBtn
            color="primary"
            size="small"
            @click="openAddChannelDialog"
          >
            <VIcon
              start
              icon="tabler-plus"
            />
            채널 추가
          </VBtn>
        </VCardTitle>

        <VDivider />

        <VCardText>
          <VDataTable
            :headers="configHeaders"
            :items="driverConfigs"
            :loading="configsLoading"
          >
            <template #item.download_driver="{ item }">
              {{ item.download_driver?.name || '-' }}
            </template>
            <template #item.actions="{ item }">
              <div class="d-flex gap-1 flex-wrap">
                <VBtn
                  size="small"
                  color="warning"
                  variant="tonal"
                  :loading="actionLoading === `clearLastCrawledDate-${item.id}`"
                  @click="confirmDriverAction('last_crawled_date를 초기화하시겠습니까?', 'clearLastCrawledDate', item)"
                >
                  크롤링 날짜 초기화
                </VBtn>
                <VBtn
                  size="small"
                  color="primary"
                  variant="tonal"
                  :loading="actionLoading === `runPeriodicEcs-${item.id}`"
                  @click="confirmDriverAction('정기 이관 ECS를 강제 실행하시겠습니까?', 'runPeriodicEcs', item)"
                >
                  정기 이관 실행
                </VBtn>
                <VBtn
                  size="small"
                  color="primary"
                  variant="tonal"
                  :loading="actionLoading === `runWidgetEcs-${item.id}`"
                  @click="confirmDriverAction('위젯 ECS를 강제 실행하시겠습니까?', 'runWidgetEcs', item)"
                >
                  위젯 실행
                </VBtn>
                <VBtn
                  size="small"
                  color="secondary"
                  variant="tonal"
                  :loading="actionLoading === `unlockSoldout-${item.id}`"
                  @click="confirmDriverAction('품절 상태를 해제하시겠습니까?', 'unlockSoldout', item)"
                >
                  품절 해제
                </VBtn>
                <VBtn
                  size="small"
                  color="secondary"
                  variant="tonal"
                  :loading="actionLoading === `updateLastCrawledDate-${item.id}`"
                  @click="runDriverAction('updateLastCrawledDate', item)"
                >
                  크롤링 날짜 동기화
                </VBtn>
                <VBtn
                  v-if="item.driver_code === 'zigzag'"
                  size="small"
                  color="error"
                  variant="tonal"
                  :loading="actionLoading === `deleteZzExpress-${item.id}`"
                  @click="confirmDriverAction('직진배송 Target을 모두 삭제하시겠습니까?', 'deleteZzExpress', item)"
                >
                  직진배송 삭제
                </VBtn>
                <VBtn
                  icon
                  size="small"
                  variant="text"
                  color="error"
                  @click="deleteDriverConfig(item)"
                >
                  <VIcon icon="tabler-trash" />
                </VBtn>
              </div>
            </template>
          </VDataTable>
        </VCardText>
      </VCard>

      <!-- 클라이언트 운영 액션 -->
      <VCard class="mb-6">
        <VCardTitle class="pa-6">
          클라이언트 운영 액션
        </VCardTitle>
        <VDivider />
        <VCardText>
          <div class="d-flex flex-wrap gap-2">
            <VBtn
              color="secondary"
              variant="tonal"
              :loading="clientActionLoading === 'updateUploadReport'"
              @click="runClientAction('updateUploadReport')"
            >
              <VIcon
                start
                icon="tabler-refresh"
              />
              업로드 리포트 갱신
            </VBtn>
            <VBtn
              color="warning"
              variant="tonal"
              :loading="clientActionLoading === 'clearUploadReadyState'"
              @click="confirmClientAction('ready 상태 문서를 삭제하시겠습니까?', 'clearUploadReadyState')"
            >
              <VIcon
                start
                icon="tabler-trash"
              />
              Ready 상태 초기화
            </VBtn>
          </div>
        </VCardText>
      </VCard>

      <!-- 목록으로 돌아가기 버튼 -->
      <div class="d-flex justify-start">
        <VBtn
          variant="outlined"
          :to="{ name: 'review-client' }"
        >
          <VIcon
            start
            icon="tabler-arrow-left"
          />
          목록으로 돌아가기
        </VBtn>
      </div>
    </template>

    <!-- 데이터 없음 -->
    <VCard v-else>
      <VCardText class="text-center pa-6">
        <VIcon
          icon="tabler-alert-circle"
          size="48"
          color="error"
        />
        <p class="mt-4">
          고객사 정보를 찾을 수 없습니다.
        </p>
        <VBtn
          color="primary"
          :to="{ name: 'review-client' }"
        >
          목록으로 돌아가기
        </VBtn>
      </VCardText>
    </VCard>

    <!-- 채널 추가 다이얼로그 -->
    <VDialog
      v-model="addChannelDialog"
      max-width="500"
    >
      <VCard>
        <VCardTitle>채널 추가</VCardTitle>
        <VCardText>
          <AppSelect
            v-model="selectedDownloadDriver"
            label="다운로드 드라이버"
            :items="availableDrivers"
            item-title="name"
            item-value="id"
            placeholder="드라이버를 선택하세요"
          />
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="addChannelDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            :loading="addingChannel"
            :disabled="!selectedDownloadDriver"
            @click="addChannel"
          >
            추가
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar.isVisible.value"
      :color="snackbar.color.value"
      :timeout="snackbar.timeout.value"
    >
      {{ snackbar.message.value }}
    </VSnackbar>
  </div>
</template>
